import {
  Modal,
  Select,
  useLocalStorageAsState,
  useScreenWidth,
} from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { EntitySearchFilterOperator } from "../../../../clients/graphqlTypes.js";
import { FullScreenToggleIconButton } from "../../../commons/button/FullScreenToggleIconButton.js";
import {
  ExpressionInputWithVariables,
  type PropertyOption,
  type VariableInputState,
} from "./ExpressionInputWithVariables.js";

interface Props<Operator extends EntitySearchFilterOperator> {
  projectId: string | null;
  docId: string;
  itemId: string;
  filterLabel: string;
  operator: Operator;
  allowedSearchFilterOperators: { label: string; value: Operator }[];
  expression: string | undefined;
  filterOptions?: (option: PropertyOption) => boolean;
  handleClose: () => void;
  /**
   * Detach the expression from this filter. Absent while the filter has none —
   * the modal is then authoring a first expression, with nothing to remove.
   */
  handleDelete?: (handleClose: () => void) => Promise<void> | void;
  handleComplete: (
    value: {
      expr: string;
      operator: Operator;
    },
    handleClose: () => void
  ) => Promise<void> | void;
}

export function SearchFilterExpressionModal<
  Operator extends EntitySearchFilterOperator,
>({
  projectId,
  docId,
  itemId,
  filterLabel,
  operator: passedOperator,
  allowedSearchFilterOperators,
  expression: passedExpression = "",
  filterOptions,
  handleClose,
  handleComplete,
  handleDelete,
}: Props<Operator>) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const { isMaxPhone } = useScreenWidth();
  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    `msys-search-filter-expression-modal-fullscreen`,
    false,
    true
  );

  const [inputState, setInputState] = React.useState<VariableInputState>({
    expression: passedExpression,
    cursorIndex: null,
  });

  const [selectedOperator, setSelectedOperator] =
    React.useState<Operator>(passedOperator);

  const handleSave = async () => {
    handleComplete(
      { expr: inputState.expression, operator: selectedOperator },
      handleClose
    );
  };

  return (
    <Modal
      title={t("Build your formula", { ns: "QuoteItem" })}
      dialogProps={
        !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
      }
      headerActions={
        !isMaxPhone ? (
          <FullScreenToggleIconButton
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
          />
        ) : undefined
      }
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: {
            variant: "text",
          },
        },
        // Removing the expression keeps the filter, carrying whatever the
        // expression last computed — only deleting the filter row removes it.
        ...(handleDelete
          ? [
              {
                label: t("Remove formula", { ns: "QuoteItem" }),
                handleClick: async () => {
                  await handleDelete(handleClose);
                },
                buttonProps: { variant: "outlined" as const },
              },
            ]
          : []),
        {
          label: t("Save", { ns: "Global" }),
          handleClick: handleSave,
          buttonProps: {
            disabled: !inputState.expression.trim(),
          },
        },
      ]}
      handleClose={handleClose}
    >
      <Stack spacing={1}>
        <Typography color={"secondary"}>{filterLabel}</Typography>
        <Select
          label={"Operator"}
          options={allowedSearchFilterOperators}
          value={selectedOperator}
          onChange={value => {
            setSelectedOperator(value as Operator);
          }}
          required
        />
        <ExpressionInputWithVariables
          label={t("Formula", { ns: "QuoteItem" })}
          itemId={itemId}
          docId={docId}
          projectId={projectId}
          inputState={inputState}
          onInputStateChange={setInputState}
          autoFocus={true}
          direction="column"
          showSelectedVariableAllowedValues={false}
          filterOptions={filterOptions}
        />
      </Stack>
    </Modal>
  );
}
